import type { FC } from 'react';
import { useCallback, useState } from 'react';
import ddt from '@wix/da-ddt';
import { useFormContext } from 'react-hook-form';
import {
  PapiStudioDiscountsCreate,
  PapiStudioDiscountsUpdate,
} from '@wix/da-papi-types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  CreateDiscountConfirmClickBiEvent,
  UpdateDiscountClickBiEvent,
} from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { useDispatch } from 'react-redux';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { discountFormAdapterNew, discountFormAdapterOld } from '../../adapter';

const logger = ddt.createLogger('discountModal');

interface Props {
  label: string;
  discountId?: number;
  onSuccess?: (response: PapiStudioDiscountsCreate | PapiStudioDiscountsUpdate) => void;
  onError?: (response) => void;
}

const SubmitDiscountFormButton: FC<Props> = ({
  label,
  discountId,
  onSuccess,
  onError,
}) => {
  const formContext = useFormContext();
  const logBiEvent = useBiLogger();
  const { handleSubmit } = formContext;
  const [isBusy, setIsBusy] = useState(false);
  const studioDiscountChanges = useFlag('studio_discounts_changes');

  const dispatch = useDispatch();

  const requestSubmit = useCallback(() => {
    handleSubmit((data: any) => {
      setIsBusy(true);
      const discountFormAdapter = studioDiscountChanges
        ? discountFormAdapterNew
        : discountFormAdapterOld;
      const transformedData = discountFormAdapter(data);
      dispatch(
        makeXHRCall({
          method: SimpleXHRMethod.POST,
          endpoint: discountId
            ? SimpleXHREndpoint.SELLER_DISCOUNT_UPDATE
            : SimpleXHREndpoint.SELLER_DISCOUNT_CREATE,
          papiBase: 'studio',
          data: { ...transformedData, discount_id: discountId },
          onSuccess: (
            response: PapiStudioDiscountsCreate | PapiStudioDiscountsUpdate
          ) => {
            logBiEvent(
              discountId
                ? BiData<UpdateDiscountClickBiEvent>({
                    evid: 455,
                    campaignid: discountId,
                    is_valid: 1,
                  })
                : BiData<CreateDiscountConfirmClickBiEvent>({
                    evid: 451,
                    campaignid: response.discount?.discountId ?? null,
                    is_valid: 1,
                  })
            );
            onSuccess && onSuccess(response);
          },
          onError: response => {
            logBiEvent(
              discountId
                ? BiData<UpdateDiscountClickBiEvent>({
                    evid: 455,
                    campaignid: discountId,
                    is_valid: 0,
                  })
                : BiData<CreateDiscountConfirmClickBiEvent>({
                    evid: 451,
                    campaignid: response.discount?.discountId ?? null,
                    is_valid: 0,
                  })
            );
            onError && onError(response);
          },
        })
      );
    }, logger.error)();
  }, [
    dispatch,
    onError,
    discountId,
    handleSubmit,
    onSuccess,
    formContext,
    logBiEvent,
    studioDiscountChanges,
  ]);

  return (
    <BrandedButton
      key="footer-save"
      size="large"
      variant="brand-green"
      onClick={requestSubmit}
      loading={isBusy}
      type="submit"
    >
      {label}
    </BrandedButton>
  );
};
SubmitDiscountFormButton.displayName = 'SubmitDiscountFormButton';

export default SubmitDiscountFormButton;
